/**
 * The Positioning & liquidity section's arithmetic — pure, so the rules the
 * mock states (`docs/mockups/macro-v2.html`, `glFund` / `dgUnwind` / `glDG`)
 * are tested without a browser.
 *
 * The server publishes the numbers struck at its own σ; the section offers
 * −1.5 / −2 / −2.5σ, so an unwind day is **re-struck here** from `moZ` (the
 * momentum z against the 60 days strictly before), never recomputed from the
 * legs' history. Funding and the spread share **one date axis**: its domain
 * starts on the spread's first day (the mock's `k0`) and ends on the later of
 * the two series' last days.
 */

import type { DegrossColumns, FundingColumns, PositioningParams } from "../api/macro-positioning";

type Num = number | null;

export const SIGMAS = [1.5, 2, 2.5] as const;
export type Sigma = (typeof SIGMAS)[number];

/** `config.sigma`, else the server's σ when it is one the control offers, else 2. */
export function effectiveSigma(configured: number | undefined, server: number | undefined): Sigma {
  const ok = (v: number | undefined): v is Sigma => SIGMAS.includes(v as Sigma);
  if (ok(configured)) return configured;
  if (ok(server)) return server;
  return 2;
}

/** Momentum below −σ **and** the crowded longs trailing most-shorted by more than `gap`%. */
export function unwindDays(moZ: readonly Num[], long: readonly Num[], short: readonly Num[], sigma: number, gap: number): boolean[] {
  return moZ.map((z, i) => {
    const l = long[i];
    const s = short[i];
    return z != null && l != null && s != null && z < -sigma && l - s < -gap;
  });
}

const lastNonNull = (xs: readonly Num[]): { i: number; v: number } | null => {
  for (let i = xs.length - 1; i >= 0; i--) {
    const v = xs[i];
    if (v != null) return { i, v };
  }
  return null;
};

export interface SpreadState {
  /** Percent below the running peak today. */
  fromPeak: number | null;
  /** The deepest drawdown in the window. */
  worst: number | null;
  /** Indices of unwind days at this σ. */
  unwind: number[];
  /** Unwind days in the last `unwindRecentDays` rows. */
  recent: number;
  /** Below the de-grossing line (−5% from peak by default). */
  degrossing: boolean;
  /** Within 2% of the window's worst, the mock's "deepest in the window". */
  deepest: boolean;
  /** `nodata` when there is no drawdown from peak to read: never "normal" by default. */
  state: "degrossing" | "normal" | "nodata";
}

/** The mock's `dgState`, for one version of the spread (our 13F basket, or GVIP). */
export function spreadState(dg: DegrossColumns, which: "13f" | "gvip", sigma: number, p: PositioningParams): SpreadState {
  const long = which === "13f" ? dg.lg : dg.gv;
  const dd = which === "13f" ? dg.dd : dg.gDd;
  const un = unwindDays(dg.moZ, long, dg.sh, sigma, p.unwindSpreadPct);
  const unwind: number[] = [];
  un.forEach((u, i) => u && unwind.push(i));
  const from = dg.date.length - p.unwindRecentDays;
  const recent = unwind.filter((i) => i >= from).length;
  const cur = lastNonNull(dd)?.v ?? null;
  const vals = dd.filter((v): v is number => v != null);
  const worst = vals.length ? Math.min(...vals) : null;
  const degrossing = cur != null && cur < p.degrossDrawdownPct;
  return {
    fromPeak: cur,
    worst,
    unwind,
    recent,
    degrossing,
    deepest: cur != null && worst != null && worst < 0 && cur <= worst * 0.98,
    state: cur == null ? "nodata" : degrossing ? "degrossing" : "normal",
  };
}

export interface FundingState {
  /** The trailing average of SOFR − IORB, bp, on the last day. */
  avg: number | null;
  last: number | null;
  lastDate: string | null;
  rrp: number | null;
  tight: boolean;
  /** Index of the widest SOFR − IORB on the axis, for its label. */
  peak: number | null;
  /** `nodata` when there is no trailing average to read: never "calm" by default. */
  state: "tight" | "calm" | "nodata";
}

/** The funding header line (the mock's `glFund`), over the rows `from` onward. */
export function fundingState(f: FundingColumns, p: PositioningParams, from = 0): FundingState {
  const sp = f.spreadBp.slice(from);
  const lastSp = lastNonNull(sp);
  const avg = lastNonNull(f.avgBp.slice(from))?.v ?? null;
  let peak: number | null = null;
  sp.forEach((v, i) => {
    if (v != null && (peak == null || v > (sp[peak] as number))) peak = i;
  });
  const tight = avg != null && avg > p.fundingTightBp;
  return {
    avg,
    state: avg == null ? "nodata" : tight ? "tight" : "calm",
    last: lastSp?.v ?? null,
    lastDate: lastSp ? f.date[from + lastSp.i] ?? null : null,
    rrp: lastNonNull(f.rrp.slice(from))?.v ?? null,
    tight,
    peak,
  };
}

/** A date string `YYYY-MM-DD` as UTC milliseconds. */
export const dayMs = (d: string): number => Date.parse(`${d.slice(0, 10)}T00:00:00Z`);

export interface SharedAxis {
  d0: number;
  d1: number;
  /** The first funding row on the axis. */
  fundingFrom: number;
}

/** The one time axis both charts are drawn on; null with nothing to draw. */
export function sharedAxis(dg: DegrossColumns, f: FundingColumns): SharedAxis | null {
  const firsts: number[] = [];
  const lasts: number[] = [];
  if (dg.date.length) {
    firsts.push(dayMs(dg.date[0]!));
    lasts.push(dayMs(dg.date[dg.date.length - 1]!));
  }
  if (f.date.length) lasts.push(dayMs(f.date[f.date.length - 1]!));
  const d0: number = firsts[0] ?? (f.date.length ? dayMs(f.date[0]!) : NaN);
  if (!Number.isFinite(d0) || !lasts.length) return null;
  const d1 = Math.max(...lasts);
  let fundingFrom = f.date.findIndex((d) => dayMs(d) >= d0);
  if (fundingFrom < 0) fundingFrom = f.date.length;
  return { d0, d1: d1 > d0 ? d1 : d0 + 86_400_000, fundingFrom };
}

/** The row on or before `t` (binary search over ascending dates), or -1. */
export function indexAtOrBefore(dates: readonly string[], t: number): number {
  let lo = 0;
  let hi = dates.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dayMs(dates[mid]!) <= t) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Quarter starts after the first month on the axis, labelled `Jan 26`. */
export function quarterTicks(d0: number, d1: number): { t: number; label: string }[] {
  const out: { t: number; label: string }[] = [];
  const s = new Date(d0);
  let y = s.getUTCFullYear();
  let m = s.getUTCMonth() + 1;
  for (;;) {
    if (m > 11) {
      m = 0;
      y++;
    }
    const t = Date.UTC(y, m, 1);
    if (t > d1) break;
    if (m % 3 === 0) out.push({ t, label: `${MON[m]} ${String(y).slice(2)}` });
    m++;
  }
  return out;
}

/** An SVG path through the non-null points; a null breaks the line. */
export function linePath(xs: readonly number[], ys: readonly Num[], y: (v: number) => number): string {
  let d = "";
  let pen = false;
  ys.forEach((v, i) => {
    if (v == null || !Number.isFinite(xs[i])) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${xs[i]!.toFixed(1)},${y(v).toFixed(1)}`;
    pen = true;
  });
  return d;
}

export interface LegSummary {
  /** The last `days` rows summed: % against SPY. */
  total: number | null;
  /** The cumulative last `spark` rows, for the card's line. */
  cum: number[];
}

/** A leg card: the mock's "20 days vs SPY" and its 60-day cumulative line. */
export function legSummary(values: readonly Num[], days = 20, spark = 60): LegSummary {
  const tail = values.slice(-days).filter((v): v is number => v != null);
  let c = 0;
  const cum = values.slice(-spark).map((v) => (c += v ?? 0));
  return { total: tail.length ? tail.reduce((s, v) => s + v, 0) : null, cum };
}

/** `+1.2` / `−3.4`, with a true minus. */
export function signed1(v: number): string {
  return `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(1)}`;
}
